/**
 * TheoremBadge — displays a single theorem's canonical name and mode.
 *
 * Single responsibility: render one DemoTheorem as a compact badge.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import type { DemoTheorem } from "../types.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  GRAY,
  MODE_COLOR,
  THEOREM_COLOR,
  withAlpha,
} from "../design-system/tokens.js";

export interface TheoremBadgeProps {
  readonly theorem: DemoTheorem;
}

/**
 * Displays a compact badge showing the theorem name, neutralization mode,
 * and generator with color coding.
 */
export const TheoremBadge = React.memo(function TheoremBadge({
  theorem,
}: TheoremBadgeProps): React.JSX.Element {
  const primaryMode = theorem.mode;
  const generatorLabel = Array.isArray(theorem.generator)
    ? theorem.generator.join("+")
    : theorem.generator;
  // `theorem.name` is a free string and may not be a known theorem name, so
  // dispatch through Object.hasOwn so TypeScript and ESLint both see the
  // runtime fallback as reachable.
  const theoremColor = Object.hasOwn(THEOREM_COLOR, theorem.name)
    ? THEOREM_COLOR[theorem.name as keyof typeof THEOREM_COLOR]
    : MODE_COLOR[primaryMode].primary;
  const dualPairStyle = theorem.dualPair ? DUAL_PAIR_COLOR[theorem.dualPair] : null;
  const compositionalityStyle = theorem.sdp ? COMPOSITIONALITY_PRINCIPLE_COLOR[theorem.sdp] : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <span
        style={{
          padding: "0.25rem 0.75rem",
          backgroundColor: withAlpha(theoremColor, "1A"),
          color: theoremColor,
          fontSize: "0.875rem",
          fontWeight: "600",
          borderRadius: "9999px",
        }}
      >
        {theorem.name}
      </span>

      <span
        style={{
          padding: "0.25rem 0.5rem",
          backgroundColor: withAlpha(MODE_COLOR[theorem.mode].primary, "1A"),
          color: MODE_COLOR[theorem.mode].dim,
          fontSize: "0.75rem",
          borderRadius: "0.25rem",
          textTransform: "uppercase",
          fontWeight: "600",
        }}
      >
        {MODE_COLOR[theorem.mode].label}
      </span>

      {theorem.dualPair && (
        <span
          style={{
            padding: "0.25rem 0.5rem",
            backgroundColor: dualPairStyle?.bg,
            color: dualPairStyle?.color,
            fontSize: "0.6875rem",
            borderRadius: "0.25rem",
            fontWeight: "600",
          }}
        >
          DP:{theorem.dualPair}
        </span>
      )}

      {theorem.sdp && (
        <span
          style={{
            padding: "0.25rem 0.5rem",
            backgroundColor: compositionalityStyle?.bg,
            color: compositionalityStyle?.color,
            fontSize: "0.6875rem",
            borderRadius: "0.25rem",
            fontWeight: "600",
          }}
        >
          SDP:{theorem.sdp}
        </span>
      )}

      <span style={{ color: GRAY[500], fontSize: "0.875rem" }}>
        {theorem.structure} · {generatorLabel}
      </span>
    </div>
  );
});
